import axios from 'axios';

// Create axios instance with default config
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// We'll set the auth token once centrally and not modify it with each request
// This prevents token conflicts with localStorage potentially being out of sync

// Supplier related API calls
export const createSupplier = async (
  name, 
  email, 
  balance = 0, 
  street = null, 
  city = null, 
  state = null, 
  country = null, 
  iban = null, 
  swift_code = null
) => {
  return await api.post('/suppliers', { 
    name, 
    email, 
    balance, 
    street, 
    city, 
    state, 
    country, 
    iban, 
    swift_code 
  });
};
export const getSuppliers = () => api.get('/suppliers');
export const deleteSupplier = async (name, email) => {
  try {
    const response = await api.delete(`/suppliers/${name}/${email}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error.message;
  }
};
export const updateSupplier = (name, email, data) => {
  // If data is FormData, set headers for multipart/form-data
  if (data instanceof FormData) {
    return api.put(`/suppliers/${name}/${email}`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
      }
    });
  } else {
    return api.put(`/suppliers/${name}/${email}`, data);
  }
};

export const createSuppliersBatch = (formData) => {
  return api.post('/suppliers/batch', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
    }
  });
};

export const uploadTodosCsv = (formData) => {
  return api.post('/todos/upload-csv', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
    }
  });
};

// Invoice related API calls
export const uploadInvoice = (formData) => api.post('/invoices/upload', formData, {
  headers: {
    'Content-Type': 'multipart/form-data',
  },
});
export const reUploadRejectedInvoice = (formData) => api.post('/invoices/upload', formData, {
  headers: {
    'Content-Type': 'multipart/form-data',
  },
});
export const getInvoices = () => api.get('/invoices');
export const getMyInvoices = () => api.get('/invoices/me');
export const getAllInvoices = () => api.get('/invoices/all');
export const approveInvoice = (supplierName, monthYear, expectedPaymentDate) => 
  api.post('/invoices/action/approve', { supplier_name: supplierName, month_year: monthYear, expected_payment_date: expectedPaymentDate });
export const markInvoiceAsPaid = async (supplierName, monthYear) => {
  try {
    console.log(`Marking invoice as paid: ${supplierName} - ${monthYear}`);
    const response = await api.post('/invoices/action/paid', { supplier_name: supplierName, month_year: monthYear });
    console.log('Mark as paid response:', response.data);
    return response;
  } catch (error) {
    console.error('Error marking invoice as paid:', error);
    console.error('Request URL:', '/invoices/action/paid');
    console.error('Request data:', { supplier_name: supplierName, month_year: monthYear });
    console.error('Error response:', error.response?.data);
    console.error('Error status:', error.response?.status);
    throw error;
  }
};
export const rejectInvoice = (supplierName, monthYear, rejectionReason) => 
  api.post('/invoices/action/reject', { 
    supplier_name: supplierName, 
    month_year: monthYear,
    rejection_reason: rejectionReason || 'Invoice rejected' 
  });
export const getDownloadUrl = (supplierName, monthYear) => 
  api.post('/invoices/action/download', { supplier_name: supplierName, month_year: monthYear });
export const getInvoiceAuditLogs = (supplierName, monthYear) =>
  api.get(`/invoices/audit-log/${supplierName}/${monthYear.replace('/', '-')}`);

// User related API calls
export const getCurrentUser = () => api.get('/me');

// Todo related API calls
export const getTodos = (supplierName) => {
  const params = supplierName ? { supplier_name: supplierName } : {};
  return api.get('/todos', { params });
};
export const createTodo = (todoData) => api.post('/todos', todoData);
export const getTodo = (supplierName, monthYear) => {
  const [month, year] = monthYear.split('/');
  return api.get(`/todos/${supplierName}/${month}/${year}`);
};
export const updateTodo = (id, supplierName, monthYear, title, description, amount, deduction, status, completed, dueDate, rejectionReason) => {
  const [month, year] = monthYear.split('/');
  const month_year = `${month}-${year}`;
  return api.put(`/todos/${supplierName}/${month_year}`, {
    title,
    description,
    amount,
    deduction,
    status,
    completed,
    due_date: dueDate,
    rejection_reason: rejectionReason
  });
};
export const updateTodoAdmin = (supplierName, monthYear, todoData) => {
  return api.put(`/todos/admin/${supplierName}/${monthYear}`, todoData);
};
export const deleteTodo = (supplierName, monthYear) => {
  // Split month_year into month and year for the URL
  const [month, year] = monthYear.split('/');
  return api.delete(`/todos/${supplierName}/${month}/${year}`);
};

// Enhanced admin function with comprehensive filtering
export const getAdminTodos = ({ 
  supplierName = null, 
  userEmail = null, 
  status = null, 
  monthYear = null 
} = {}) => {
  const params = {};
  if (supplierName) params.supplier_name = supplierName;
  if (userEmail) params.user_email = userEmail;
  if (status) params.todo_status = status;
  if (monthYear) params.month_year = monthYear;
  
  return api.get('/todos/admin', { params });
};

export const createUser = async (email, roles = ['supplier'], domain = '') => {
  return await api.post('/users', { email, roles, domain });
};

// Supplier contract download URL
export const getSupplierContractUrl = (name, email) =>
  api.get(`/suppliers/${encodeURIComponent(name)}/${encodeURIComponent(email)}/contract`);

export const pingTodoSupplier = (supplierName, monthYear, message) => {
  // Normalize monthYear to MM-YYYY for the URL
  let [month, year] = monthYear.includes('/') ? monthYear.split('/') : monthYear.split('-');
  month = month.padStart(2, '0');
  const urlMonthYear = `${month}-${year}`;
  return api.post(`/todos/admin/${encodeURIComponent(supplierName)}/${urlMonthYear}/ping`, { message });
};

// Export the axios instance as well
export { api };
export default api;